import getCustomerApi from '@/api/get/customer/get-customer';
import Card from '@/components/card/card-component';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import Pill from '@/components/pill/pill-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import { useQuery } from '@/hooks/use-api';
import {
  BodyText,
  Heading3Text,
  SmallTitle,
  SubheaderText,
  Spacer,
} from '@/utils/global-fonts';
import { toSentenceCase } from '@/utils/utils';
import { useParams } from 'react-router-dom';
import Styled from './customer-view-style';

/**
 *
 * @returns {JSX.Element} The rendered Customer Edit page component.
 */
const CustomerView = () => {
  const { customerId } = useParams();
  const { data, loading } = useQuery(getCustomerApi, customerId);

  if (loading) {
    return (
      <Styled.Div>
        <Loader loading />
      </Styled.Div>
    );
  }

  return (
    <>
      <Card padding="12px 16px">
        <Col xs={12}>
          <Styled.StyledRow
            direction="row"
            align="center"
            justify="space-between"
          >
            <Styled.Span>
              <Heading3Text className="title">Details</Heading3Text>
            </Styled.Span>
            <StatusIndicator status={data?.customer?.status} />
          </Styled.StyledRow>
          <Row>
            <Col xs={6}>
              <Styled.DivStyle>
                <SmallTitle>CUSTOMER NAME</SmallTitle>
                <BodyText>{toSentenceCase(data?.customer?.name)}</BodyText>
              </Styled.DivStyle>
            </Col>
            <Col xs={6}>
              <Styled.DivStyle>
                <SmallTitle>ADDRESS</SmallTitle>
                <BodyText>{toSentenceCase(data?.customer?.address)}</BodyText>
              </Styled.DivStyle>
            </Col>
            <Col xs={6}>
              <Styled.DivStyle>
                <SmallTitle>PHONE NUMBER</SmallTitle>
                <BodyText>{data?.customer?.contact_number}</BodyText>
              </Styled.DivStyle>
            </Col>
          </Row>
          <Styled.StyledRow
            direction="row"
            align="center"
            justify="space-between"
          >
            <SubheaderText className="title">Contact Person/s</SubheaderText>
          </Styled.StyledRow>

          {data?.contactPerson?.map((cP, i) => (
            <>
              <Row>
                <Col xs={6}>
                  <Styled.DivStyle>
                    <SmallTitle>NAME</SmallTitle>
                    <Styled.CustomRow>
                      <BodyText>{toSentenceCase(cP?.name)}</BodyText>
                      {cP.default_recipient_of_quotes && (
                        <Pill type="default" />
                      )}
                    </Styled.CustomRow>
                  </Styled.DivStyle>
                </Col>
                <Col xs={6}>
                  <Styled.DivStyle>
                    <SmallTitle>EMAIL ADDRESS</SmallTitle>
                    <BodyText>{toSentenceCase(cP.email)}</BodyText>
                  </Styled.DivStyle>
                </Col>
                <Col xs={6}>
                  <Styled.DivStyle>
                    <SmallTitle>PHONE NUMBER</SmallTitle>

                    <Styled.PhoneNumberContainer>
                      <Styled.PhoneInput
                        name="contact_number"
                        disabled
                        value={cP?.contact_number}
                      />
                      <BodyText>{cP?.contact_number}</BodyText>
                    </Styled.PhoneNumberContainer>
                  </Styled.DivStyle>
                </Col>
                <Col xs={6}>
                  <Styled.DivStyle>
                    <SmallTitle>POSITION</SmallTitle>
                    <BodyText>{cP.position}</BodyText>
                  </Styled.DivStyle>
                </Col>
                {cP?.superior && (
                  <Col xs={6}>
                    <Styled.DivStyle>
                      <SmallTitle>SUPERIOR</SmallTitle>
                      <BodyText>
                        {
                          data?.contactPerson?.filter(
                            (p) => p.id === cP.superior,
                          )[0]?.name
                        }
                      </BodyText>
                    </Styled.DivStyle>
                  </Col>
                )}
              </Row>
              {i + 1 < data?.contactPerson?.length && <Styled.Hr />}
            </>
          ))}
        </Col>
      </Card>
      <Spacer />
      <Card padding="12px 16px">
        <Styled.StyledRow
          direction="row"
          align="center"
          justify="space-between"
        >
          <SubheaderText className="title">
            Customer Operating Sites
          </SubheaderText>
        </Styled.StyledRow>

        {data?.operatingSite?.map((oS, i) => (
          <>
            <Row>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>SITE NAME</SmallTitle>
                  <BodyText>{toSentenceCase(oS?.name)}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>COUNTRY</SmallTitle>
                  <BodyText>{toSentenceCase(oS.country)}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>NEAREST TOWN</SmallTitle>
                  <BodyText>{oS.nearest_town}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>SITE COMMODITY</SmallTitle>
                  <BodyText>{oS.site_commodity}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>OTHER</SmallTitle>
                  <BodyText>{oS.other}</BodyText>
                </Styled.DivStyle>
              </Col>
            </Row>
            {i + 1 < data?.operatingSite?.length && <Styled.Hr />}
          </>
        ))}
      </Card>
    </>
  );
};

export default CustomerView;
